import { type UseMutationOptions, type UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'
import { PRODUCT_ENDPOINTS } from './product.endpoints'
import type {
  Category,
  PaginatedProductReviewResponse,
  ProductDetail,
  ProductExploreParams,
  ProductExploreResponse,
  ProductReviewRequest,
  ProductReviewResponse,
} from './product.types'

// --- Service Functions ---

export const getProductExplore = async (params?: ProductExploreParams): Promise<ProductExploreResponse> => {
  const response = await apiClient.get<ProductExploreResponse>(PRODUCT_ENDPOINTS.EXPLORE_LIST, { params })
  return response.data
}

export const getProductDetail = async (productSlug: string): Promise<ProductDetail> => {
  const response = await apiClient.get<ProductDetail>(PRODUCT_ENDPOINTS.EXPLORE_DETAIL(productSlug))
  return response.data
}

export interface PostProductReviewProps {
  productSlug: string
  data: ProductReviewRequest
}

export const postProductReview = async ({
  productSlug,
  data,
}: PostProductReviewProps): Promise<ProductReviewResponse> => {
  const formData = new FormData()
  formData.append('title', data.title)
  formData.append('review_text', data.review_text)
  formData.append('rating', data.rating.toString())

  if (data.images) {
    for (const image of data.images) {
      formData.append('images', image)
    }
  }

  const response = await apiClient.post<ProductReviewResponse>(PRODUCT_ENDPOINTS.ADD_REVIEW(productSlug), formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

export const getProductReviews = async (productSlug: string, page = 1): Promise<PaginatedProductReviewResponse> => {
  const response = await apiClient.get<PaginatedProductReviewResponse>(PRODUCT_ENDPOINTS.REVIEWS(productSlug), {
    params: { page },
  })
  return response.data
}

// --- TanStack Query Hooks ---

export const PRODUCT_QUERY_KEYS = {
  DETAIL: ['product', 'detail'],
  EXPLORE: ['product', 'explore'],
  REVIEWS: ['product', 'reviews'],
} as const

export const useProductDetailQuery = (
  productSlug: string,
  options?: Omit<UseQueryOptions<ProductDetail>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: [...PRODUCT_QUERY_KEYS.DETAIL, productSlug],
    queryFn: () => getProductDetail(productSlug),
    ...options,
  })
}

export const useProductReviewsQuery = (
  productSlug: string,
  page = 1,
  options?: Omit<UseQueryOptions<PaginatedProductReviewResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: [...PRODUCT_QUERY_KEYS.REVIEWS, productSlug, page],
    queryFn: () => getProductReviews(productSlug, page),
    ...options,
  })
}

export const useProductReviewMutation = (
  options?: Omit<UseMutationOptions<ProductReviewResponse, Error, PostProductReviewProps>, 'mutationFn'>,
) => {
  return useMutation({
    mutationFn: postProductReview,
    ...options,
  })
}

export const useProductExploreQuery = (
  params?: ProductExploreParams,
  options?: Omit<UseQueryOptions<ProductExploreResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: [...PRODUCT_QUERY_KEYS.EXPLORE, params],
    queryFn: () => getProductExplore(params),
    ...options,
  })
}

export const getProductCategories = async (): Promise<Category[]> => {
  const response = await apiClient.get<Category[]>(PRODUCT_ENDPOINTS.CATEGORIES)
  return response.data
}

export const useProductCategoriesQuery = (options?: Omit<UseQueryOptions<Category[]>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['product', 'categories'],
    queryFn: getProductCategories,
    ...options,
  })
}
